import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Period } from '../api';

export type BoardShiftType = {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  color: string | null;
  sort_order: number;
};

export type BoardStaff = {
  id: string;
  name: string;
  job_title: string | null;
  job_title_id: string | null;
};

export type BoardRequirement = {
  shift_type_id: string;
  weekday: number;
  job_title_id: string | null;
  required_count: number;
};

export type BoardAssignment = {
  id: string;
  date: string;
  shift_type_id: string;
  employee_id: string;
  source: 'manual' | 'ai' | 'swap';
  /** null = לפי הרצועה */
  start_time: string | null;
  end_time: string | null;
  /** אחרי נפילה לרצועה, כפי שהשרת חישב */
  effective_start: string;
  effective_end: string;
};

export type BoardConstraint = {
  employee_id: string;
  date: string;
  shift_type_id: string | null;
  type_id: string;
  is_blocking: boolean;
  color: string | null;
  name: string;
  note: string | null;
};

/** סגירה או הערה שחלה על יום, או על משמרת אחת בתוכו. */
export type BoardClosure = {
  date: string;
  /** null = כל היום */
  shift_type_id: string | null;
  is_closed: boolean;
  label: string;
  note: string | null;
};

export type Board = {
  period: Period;
  /** שם הסניף, כדי שהמנהל ידע מה הוא עורך */
  branch_name: string | null;
  days: string[];
  shift_types: BoardShiftType[];
  staff: BoardStaff[];
  requirements: BoardRequirement[];
  assignments: BoardAssignment[];
  constraints: BoardConstraint[];
  /** נוסף במיגרציה 013. ייתכן חסר בתשובה משרת שעוד לא נפרס. */
  closures?: BoardClosure[];
  submitted: string[];
};

/** שומר על פעולות הרסניות: מזהה חייב להיות uuid אמיתי. */
export function isUuid(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  );
}

const boardKey = (periodId: string) => ['schedule-board', periodId] as const;
const PERIODS_KEY = ['admin-periods'] as const;

export function usePeriods() {
  return useQuery({
    queryKey: PERIODS_KEY,
    queryFn: async (): Promise<(Period & { branches: { name: string } | null })[]> => {
      // משלים את חלון השבועות לפני הקריאה, כדי שהרשימה תהיה מלאה
      // מיד ולא תמתין לריצת ה cron הלילית (מיגרציה 038).
      //
      // כישלון כאן אינו מפיל את המסך: עדיף להציג את מה שקיים מאשר
      // מסך שגיאה בגלל השלמה שלא הצליחה.
      const { error: fillFailed } = await supabase.rpc('ensure_schedule_periods');
      if (fillFailed) console.warn('השלמת שבועות נכשלה', fillFailed.message);

      const { data, error } = await supabase
        .from('schedule_periods')
        .select(
          'id, branch_id, start_date, end_date, constraints_deadline, status, published_at, publish_at, branches ( name )',
        )
        .order('start_date', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as (Period & { branches: { name: string } | null })[];
    },
  });
}

export function useBoard(periodId: string) {
  return useQuery({
    queryKey: boardKey(periodId),
    enabled: periodId !== '',
    queryFn: async (): Promise<Board | null> => {
      const { data, error } = await supabase.rpc('schedule_board', { p_period: periodId });
      if (error) throw error;
      return (data as unknown as Board) ?? null;
    },
  });
}

/** מה יימחק יחד עם התקופה. נספר לפני המחיקה, כדי שהאישור יהיה אמיתי. */
export type PeriodImpact = {
  constraints: number;
  submissions: number;
  assignments: number;
};

export function usePeriodImpact(periodId: string | null) {
  return useQuery({
    queryKey: ['period-impact', periodId],
    enabled: periodId !== null,
    queryFn: async (): Promise<PeriodImpact> => {
      const id = periodId as string;
      const count = async (
        table: 'employee_constraints' | 'constraint_submissions' | 'shift_assignments',
      ) => {
        const { count: rows, error } = await supabase
          .from(table)
          .select('*', { count: 'exact', head: true })
          .eq('period_id', id);
        if (error) throw error;
        return rows ?? 0;
      };
      const [constraints, submissions, assignments] = await Promise.all([
        count('employee_constraints'),
        count('constraint_submissions'),
        count('shift_assignments'),
      ]);
      return { constraints, submissions, assignments };
    },
  });
}

export function usePeriodMutations(periodId: string, currentEmployeeId: string | undefined) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: boardKey(periodId) });
    void queryClient.invalidateQueries({ queryKey: PERIODS_KEY });
  };

  const create = useMutation({
    mutationFn: async (input: {
      branch_id: string;
      start_date: string;
      end_date: string;
      constraints_deadline: string;
    }) => {
      const { data, error } = await supabase
        .from('schedule_periods')
        .insert({ ...input, created_by: currentEmployeeId ?? null })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PERIODS_KEY }),
  });

  const assign = useMutation({
    mutationFn: async (input: {
      branch_id: string;
      date: string;
      shift_type_id: string;
      employee_id: string;
      /** null = לפי שעות הרצועה */
      start_time?: string | null;
      end_time?: string | null;
    }) => {
      const { error } = await supabase.from('shift_assignments').insert({
        period_id: periodId,
        ...input,
        start_time: input.start_time || null,
        end_time: input.end_time || null,
        source: 'manual',
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** שינוי שעות של שיבוץ קיים, בלי להסיר ולשבץ מחדש. */
  const setHours = useMutation({
    mutationFn: async (input: {
      id: string;
      start_time: string | null;
      end_time: string | null;
    }) => {
      const { error } = await supabase
        .from('shift_assignments')
        .update({ start_time: input.start_time || null, end_time: input.end_time || null })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const unassign = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase.from('shift_assignments').delete().eq('id', assignmentId);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const publish = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('publish_schedule', { p_period: periodId });
      if (error) throw error;
      return data as unknown as number;
    },
    onSuccess: refresh,
  });

  /**
   * תזמון הפרסום למועד עתידי.
   *
   * השרת הוא זה שמפרסם, ולא הדפדפן: מי שסוגר את המחשב בחמישי
   * בצהריים עדיין רוצה שהסידור ייצא בשמונה בערב.
   */
  const schedulePublish = useMutation({
    mutationFn: async (at: Date) => {
      const { data, error } = await supabase.rpc('schedule_publish_at', {
        p_period: periodId,
        p_at: at.toISOString(),
      });
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: refresh,
  });

  /**
   * פתיחה מחדש של סידור שפורסם.
   *
   * השיבוצים נעלמים מהעובדים עד הפרסום הבא, ולכן זו פעולה שנשאלת
   * עליה שאלה ולא נלחצת בטעות.
   */
  const reopenSchedule = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('reopen_schedule', { p_period: periodId });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const cancelPublish = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('cancel_publish_at', { p_period: periodId });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const closeDay = useMutation({
    mutationFn: async (input: {
      branch_id: string | null;
      date: string | null;
      weekday: number | null;
      shift_type_id: string | null;
      is_closed: boolean;
      label: string;
      note?: string | null;
    }) => {
      const { error } = await supabase
        .from('schedule_closures')
        .insert({ ...input, created_by: currentEmployeeId ?? null });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const reopenDay = useMutation({
    mutationFn: async (input: {
      branch_id: string;
      date: string;
      shift_type_id: string | null;
    }) => {
      // מוחקים רק כלל של התאריך הזה. כלל קבוע נערך במסך ההגדרות,
      // כדי שלא יימחק בטעות כלל שחל על כל השבועות.
      let query = supabase
        .from('schedule_closures')
        .delete()
        .eq('date', input.date)
        .eq('branch_id', input.branch_id);
      query =
        input.shift_type_id === null
          ? query.is('shift_type_id', null)
          : query.eq('shift_type_id', input.shift_type_id);
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removePeriod = useMutation({
    mutationFn: async (id: string) => {
      // מחיקה בלי פילטר תקין מוחקת את כל התקופות. השורה הזו מוודאת
      // שהמזהה אמיתי לפני שיוצאת בקשה, כי זו פעולה בלתי הפיכה
      // שמפילה בשרשור אילוצים, הגשות, שיבוצים והרצות AI.
      if (!isUuid(id)) throw new Error('מזהה תקופה לא תקין. המחיקה בוטלה.');

      const { error } = await supabase.from('schedule_periods').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PERIODS_KEY }),
  });

  return {
    create,
    assign,
    setHours,
    unassign,
    publish,
    schedulePublish,
    cancelPublish,
    reopenSchedule,
    closeDay,
    reopenDay,
    removePeriod,
  };
}

/**
 * אזהרות על השיבוץ (PRD 5.4.4). הכל נגזר מהלוח, בלי קריאה נוספת.
 */
export type Warning = { kind: 'understaffed' | 'blocked' | 'overlap'; text: string };

/**
 * הסגירה שחלה על תא, אם יש. סגירה של כל היום גוברת על סגירה של
 * משמרת אחת, כי אם היום סגור אין מה לדבר על משמרת בתוכו.
 */
export function closureFor(
  board: Board,
  date: string,
  shiftTypeId: string | null,
): BoardClosure | undefined {
  // closures נוסף ל RPC במיגרציה 013. לוח שהגיע משרת ישן, או מ cache
  // שנשמר לפני הפריסה, לא מכיל אותו, והלוח לא אמור לקרוס בגלל זה.
  const onDate = (board.closures ?? []).filter((c) => c.date === date);
  return (
    onDate.find((c) => c.shift_type_id === null) ??
    (shiftTypeId === null ? undefined : onDate.find((c) => c.shift_type_id === shiftTypeId))
  );
}

export function cellWarnings(board: Board, date: string, shiftTypeId: string): Warning[] {
  // יום סגור לא "חסר עובדים". אזהרת חוסר עליו היא רעש
  if (closureFor(board, date, shiftTypeId)?.is_closed) return [];

  const warnings: Warning[] = [];
  const weekday = new Date(date).getDay();

  const assigned = board.assignments.filter(
    (a) => a.date === date && a.shift_type_id === shiftTypeId,
  );

  const required = board.requirements
    .filter((r) => r.shift_type_id === shiftTypeId && r.weekday === weekday)
    .reduce((sum, r) => sum + r.required_count, 0);

  const missing = required - assigned.length;
  if (missing > 0) {
    warnings.push({
      kind: 'understaffed',
      // התאמה בעברית: "חסר עובד אחד", לא "חסרים 1 עובדים"
      text: missing === 1 ? 'חסר עובד אחד' : `חסרים ${missing} עובדים`,
    });
  }

  for (const assignment of assigned) {
    const blocking = board.constraints.find(
      (c) =>
        c.employee_id === assignment.employee_id &&
        c.date === date &&
        c.is_blocking &&
        (c.shift_type_id === null || c.shift_type_id === shiftTypeId),
    );
    if (blocking) {
      const name = board.staff.find((s) => s.id === assignment.employee_id)?.name ?? '';
      warnings.push({ kind: 'blocked', text: `${name} סימן/ה "${blocking.name}"` });
    }

    // אותו עובד בשתי משמרות באותו יום
    const sameDay = board.assignments.filter(
      (a) => a.date === date && a.employee_id === assignment.employee_id,
    );
    if (sameDay.length > 1 && sameDay[0].id === assignment.id) {
      const name = board.staff.find((s) => s.id === assignment.employee_id)?.name ?? '';
      warnings.push({ kind: 'overlap', text: `${name} משובץ/ת ביותר ממשמרת אחת ביום הזה` });
    }
  }

  return warnings;
}

/** האילוץ של עובד לתא מסוים, אם יש. */
export function constraintFor(
  board: Board,
  employeeId: string,
  date: string,
  shiftTypeId: string,
): BoardConstraint | undefined {
  return board.constraints.find(
    (c) =>
      c.employee_id === employeeId &&
      c.date === date &&
      (c.shift_type_id === null || c.shift_type_id === shiftTypeId),
  );
}

/**
 * השעות של שיבוץ, בניסוח שבו הרשת כותבת אותן היום בגיליון:
 * "עד 16:00" כשרק הסיום שונה מהרצועה, "מ 15:00" כשרק ההתחלה שונה,
 * שעות מלאות כששניהם שונים, וריק כששניהם לפי הרצועה.
 *
 * זה לא קישוט: תא עם חמישה אנשים ושעות מלאות לכל אחד הופך ללא קריא,
 * וזו בדיוק הסיבה שהם כותבים ככה בעצמם.
 */
export function hoursLabel(
  assignment: Pick<
    BoardAssignment,
    'start_time' | 'end_time' | 'effective_start' | 'effective_end'
  >,
): string {
  const short = (time: string) => time.slice(0, 5);
  const from = assignment.start_time !== null;
  const to = assignment.end_time !== null;

  if (from && to) return `${short(assignment.effective_start)}–${short(assignment.effective_end)}`;
  if (to) return `עד ${short(assignment.effective_end)}`;
  if (from) return `מ ${short(assignment.effective_start)}`;
  return '';
}

/**
 * הצעת סידור (PRD 5.5).
 *
 * שתי פעולות נפרדות בכוונה: ההצעה רק מחשבת ושומרת הרצה, והטעינה
 * היא מה שנוגע בסידור. ה AI אף פעם לא מפרסם לבד, וגם לא כותב לבד.
 */
export type Suggestion = {
  run_id: string;
  filled: number;
  gaps: number;
  explanation: string;
  shortages: { date: string; shift_type_id: string; missing: number }[];
};

export function useSuggestMutations(periodId: string) {
  const queryClient = useQueryClient();

  const suggest = useMutation({
    mutationFn: async (): Promise<Suggestion> => {
      const { data, error } = await supabase.rpc('suggest_schedule', { p_period: periodId });
      if (error) throw error;
      return data as unknown as Suggestion;
    },
  });

  const apply = useMutation({
    mutationFn: async (runId: string): Promise<number> => {
      const { data, error } = await supabase.rpc('apply_schedule_suggestion', { p_run: runId });
      if (error) throw error;
      return (data ?? 0) as number;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boardKey(periodId) });
    },
  });

  return { suggest, apply };
}
